import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { FilePdf, FloppyDisk, ArrowLeft, PaperPlaneTilt } from "@phosphor-icons/react";
import { api, formatApiError, TOKEN_KEY } from "../lib/api";
import { devWarn } from "../lib/log";
import { toast } from "sonner";
import { ROOF_TYPES } from "../lib/roofTypes";
import { seedSectionsForRoofType, newBlankSection } from "../lib/roofPresets";
import RoofPlanSVG from "../components/RoofPlanSVG";
import EmailQuoteDialog from "../components/EmailQuoteDialog";
import PresetPicker from "../components/quote-builder/PresetPicker";
import SectionsManager from "../components/quote-builder/SectionsManager";
import MaterialsTable from "../components/quote-builder/MaterialsTable";
import TotalsSidebar from "../components/quote-builder/TotalsSidebar";
import SpecificationsPanel from "../components/quote-builder/SpecificationsPanel";
import ComplianceOverrideModal from "../components/quote-builder/ComplianceOverrideModal";

const STATUS_OPTIONS = ["Draft", "Sent", "Accepted", "In Progress", "Completed", "Rejected"];

const DEFAULT_QUOTE = {
  customer_id: "",
  title: "Roofing Works",
  roof_type: "gable-to-gable",
  roof_category: "Pitched",
  site_address: "",
  sections: [],
  // legacy measurements kept for backward-compat read; new quotes write sections only
  measurements: { length: 8, width: 6, pitch: 35, overhang: 0.3, ridge_length: null, hip_length: null, valley_length: null, eaves_length: null },
  materials: [],
  labour_hours: 24,
  labour_rate: 35,
  scaffolding: 450,
  waste_removal: 200,
  overheads_pct: 10,
  profit_margin_pct: 20,
  vat_pct: 20,
  vat_enabled: true,
  deposit_pct: 25,
  status: "Draft",
  notes: "",
  terms: "",
  compliance_overrides: [],
};

const newLineUid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function Step({ n, label, active, done, onClick }) {
  return (
    <button onClick={onClick} className={`flex items-center gap-2.5 px-2 py-1.5 text-left ${active ? "" : "opacity-70"}`}>
      <div className={`w-7 h-7 grid place-items-center font-mono-data text-xs font-bold rounded-sm border ${active ? "bg-[var(--rqp-orange)] text-white border-[var(--rqp-orange)]" : done ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-600"}`}>
        {done ? "✓" : n}
      </div>
      <div className="text-xs font-semibold uppercase tracking-wider">{label}</div>
    </button>
  );
}

export default function QuoteBuilder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const editing = id && id !== "new";

  const [quote, setQuote] = useState(DEFAULT_QUOTE);
  const [customers, setCustomers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [preview, setPreview] = useState({ calculations: {}, summary: {} });
  const [audit, setAudit] = useState({ warnings: [], summary: { critical: 0, warning: 0, info: 0 } });
  const [specs, setSpecs] = useState({ per_section: [], aggregate: {} });
  const [overrideModal, setOverrideModal] = useState({ open: false, warning: null });
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [existingId, setExistingId] = useState(editing ? id : null);
  const [quoteNumber, setQuoteNumber] = useState("");
  const [dragIdx, setDragIdx] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);

  // Load lookups + existing quote
  useEffect(() => {
    (async () => {
      try {
        const [c, m] = await Promise.all([api.get("/customers"), api.get("/materials")]);
        setCustomers(c.data);
        setMaterials(m.data);

        if (editing) {
          const { data } = await api.get(`/quotes/${id}`);
          // Migrate legacy quotes (measurements but no sections) into sections
          let sections = (data.sections || []).map((s) => ({ ...s, id: s.id || newLineUid() }));
          if (!sections.length && data.measurements && (data.measurements.length || data.measurements.width)) {
            const cos = Math.cos(((data.measurements.pitch || 35) * Math.PI) / 180);
            const etr = cos ? (data.measurements.width || 6) / 2 / cos : 4.5;
            sections = [{
              ...newBlankSection("Main roof"),
              roof_type: data.roof_type || "gable-to-gable",
              length: data.measurements.length || 8,
              eave_to_ridge: Math.round(etr * 100) / 100,
              pitch: data.measurements.pitch || 35,
              overhang: data.measurements.overhang || 0.3,
              slope_coverage: "both",
            }];
          }
          setQuote({
            ...DEFAULT_QUOTE,
            ...data,
            sections,
            materials: (data.materials || []).map((mt) => ({ ...mt, _uid: mt._uid || newLineUid() })),
          });
          setQuoteNumber(data.quote_number || "");
          setStep(5);
        } else {
          const customerId = params.get("customer");
          const roofId = params.get("roof");
          setQuote((q) => ({
            ...q,
            customer_id: customerId || c.data[0]?.id || "",
            roof_type: roofId || q.roof_type,
            roof_category: ROOF_TYPES.find((rt) => rt.id === (roofId || q.roof_type))?.category || q.roof_category,
            sections: roofId ? seedSectionsForRoofType(roofId) : [],
          }));
        }
      } catch (e) {
        toast.error("Failed to load data");
      }
    })();
  }, [id]);

  // Auto-fill wind_zone on every section from the customer's postcode.
  // Fires whenever the customer selection changes — only sets the field
  // on sections that don't already have a wind_zone set, so manual user
  // overrides are preserved.
  useEffect(() => {
    if (!quote.customer_id || !customers.length) return;
    const cust = customers.find((c) => c.id === quote.customer_id);
    const pc = cust?.postcode;
    if (!pc) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/compliance/wind-zone?postcode=${encodeURIComponent(pc)}`);
        if (cancelled || !data?.zone) return;
        setQuote((q) => ({
          ...q,
          sections: (q.sections || []).map((s) => ({
            ...s,
            wind_zone: s.wind_zone || data.zone,
          })),
        }));
        if (!data.fallback) {
          toast.success(`Postcode ${pc} → ${data.zone} (${data.region.split(" / ")[0]})`, { duration: 2500 });
        }
      } catch (e) {
        // Wind-zone lookup is best-effort — postcode may be unmapped or
        // the user is offline. Log for diagnostics; never block the UI.
        devWarn("Wind-zone lookup failed:", e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, [quote.customer_id, customers]);

  // Live preview + audit + specifications (debounced)
  useEffect(() => {
    if (!quote.customer_id) return;
    const t = setTimeout(async () => {
      try {
        const [p, a, sp] = await Promise.all([
          api.post("/quotes/preview", quote),
          api.post("/quotes/audit", quote).catch(() => ({ data: { warnings: [], summary: {} } })),
          api.post("/compliance/specifications", quote).catch(() => ({ data: { per_section: [], aggregate: {} } })),
        ]);
        setPreview(p.data);
        setAudit(a.data || { warnings: [], summary: {} });
        setSpecs(sp.data || { per_section: [], aggregate: {} });
      } catch (e) {
        devWarn("Quote preview failed:", e?.message || e);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [quote]);

  const setField = (k, v) => setQuote((q) => ({ ...q, [k]: v }));

  const onPickPreset = (roofId, category) => {
    setQuote((q) => ({
      ...q,
      roof_type: roofId,
      roof_category: category || q.roof_category,
      // Seed sections only if user has no existing sections, or replace if blank/very new
      sections: q.sections.length === 0 ? seedSectionsForRoofType(roofId) : q.sections,
    }));
    setStep(3);
  };

  const onPickPresetReplace = (roofId, category) => {
    setQuote((q) => ({
      ...q,
      roof_type: roofId,
      roof_category: category || q.roof_category,
      sections: seedSectionsForRoofType(roofId),
    }));
    setStep(3);
  };

  const onStartBlank = () => {
    setQuote((q) => ({ ...q, sections: q.sections.length === 0 ? [newBlankSection("Main section")] : q.sections }));
    setStep(3);
  };

  const onSectionsChange = (sections) => setQuote((q) => ({ ...q, sections }));

  const addMaterialFromCatalog = (mat) => {
    const area = preview?.calculations?.roof_area_m2 || 0;
    let qty = 0;
    if (mat.coverage_per_unit > 0) qty = Math.ceil((area / mat.coverage_per_unit) * (1 + (mat.waste_pct || 0) / 100));
    setQuote((q) => ({
      ...q,
      materials: [...q.materials, { _uid: newLineUid(), material_id: mat.id, name: mat.name, manufacturer: mat.manufacturer, category: mat.category, unit: mat.unit, quantity: qty || 1, price: mat.price }],
    }));
  };

  const removeMaterial = (idx) => setQuote((q) => ({ ...q, materials: q.materials.filter((_, i) => i !== idx) }));
  const updateMaterialRow = (idx, k, v) =>
    setQuote((q) => ({
      ...q,
      materials: q.materials.map((m, i) => (i === idx ? { ...m, [k]: k === "quantity" || k === "price" ? (v === "" ? "" : Number(v)) : v } : m)),
    }));
  const reorderMaterial = (from, to) => {
    if (from === to || from == null || to == null) return;
    setQuote((q) => {
      const next = [...q.materials];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return { ...q, materials: next };
    });
  };

  const save = async () => {
    if (!quote.customer_id) { toast.error("Pick a customer"); setStep(1); return; }
    if (!quote.sections || quote.sections.length === 0) { toast.error("Add at least one section"); setStep(2); return; }
    setSaving(true);
    try {
      const payload = { ...quote };
      ["labour_hours", "labour_rate", "scaffolding", "waste_removal", "overheads_pct", "profit_margin_pct", "vat_pct"].forEach((k) => {
        payload[k] = Number(payload[k]) || 0;
      });
      payload.materials = (payload.materials || []).map(({ _uid, ...m }) => m);
      // strip local-only `id` on sections (server tolerates it but cleaner)
      payload.sections = (payload.sections || []).map(({ id: _id, ...s }) => s);
      if (existingId) {
        const { data } = await api.put(`/quotes/${existingId}`, payload);
        toast.success("Quote updated");
        setQuote({
          ...data,
          sections: (data.sections || []).map((s) => ({ ...s, id: newLineUid() })),
          materials: (data.materials || []).map((m) => ({ ...m, _uid: newLineUid() })),
        });
        setQuoteNumber(data.quote_number);
      } else {
        const { data } = await api.post("/quotes", payload);
        toast.success(`Quote ${data.quote_number} created`);
        setExistingId(data.id);
        setQuoteNumber(data.quote_number);
        navigate(`/quotes/${data.id}`, { replace: true });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    if (!existingId) { await save(); return; }
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/quotes/${existingId}/pdf`;
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u; a.download = `quote-${quoteNumber || existingId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(u);
    } catch {
      toast.error("Failed to download PDF");
    }
  };

  const updateStatus = async (status) => {
    if (!existingId) return;
    try {
      const { data } = await api.patch(`/quotes/${existingId}/status`, { status });
      setQuote((q) => ({ ...q, status: data.status }));
      toast.success(`Status → ${status}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const selectedRoof = ROOF_TYPES.find((r) => r.id === quote.roof_type);
  const perSection = preview?.calculations?.per_section || [];

  // Memoised lookups for child components — avoid creating fresh object
  // identities on every render (which would defeat React.memo and trigger
  // unnecessary effects inside EmailQuoteDialog / the customer card).
  const emailDialogQuote = useMemo(
    () => ({ id: existingId, quote_number: quoteNumber, status: quote.status }),
    [existingId, quoteNumber, quote.status],
  );
  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === quote.customer_id),
    [customers, quote.customer_id],
  );

  return (
    <div className="p-6 lg:p-10 max-w-[1400px]">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div className="flex items-center gap-3">
          <Link to="/quotes" className="btn-secondary !px-2" data-testid="quote-back"><ArrowLeft size={14} weight="bold" /></Link>
          <div>
            <span className="hero-rule" />
            <div className="overline">{existingId ? quoteNumber : "Drafting"}</div>
            <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2">
              {existingId ? "Edit Quote" : "New Quote"}
            </h1>
          </div>
        </div>
        <div className="flex gap-2">
          {existingId && (
            <select value={quote.status} onChange={(e) => updateStatus(e.target.value)} className="rqp w-auto" data-testid="quote-builder-status">
              {STATUS_OPTIONS.map((s) => <option key={s}>{s}</option>)}
            </select>
          )}
          <button onClick={save} disabled={saving} className="btn-secondary flex items-center gap-2" data-testid="quote-save">
            <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
          </button>
          {existingId && (
            <button onClick={() => setEmailOpen(true)} className="btn-secondary flex items-center gap-2" data-testid="quote-email-button">
              <PaperPlaneTilt size={14} weight="bold" /> Email PDF
            </button>
          )}
          <button onClick={downloadPdf} className="btn-primary flex items-center gap-2" data-testid="quote-pdf">
            <FilePdf size={14} weight="bold" /> Export PDF
          </button>
        </div>
      </div>

      {/* Stepper */}
      <div className="card-elev p-2 mb-4 flex flex-wrap items-center" data-testid="quote-stepper">
        <Step n={1} label="Customer" active={step === 1} done={!!quote.customer_id && step > 1} onClick={() => setStep(1)} />
        <Step n={2} label="Preset" active={step === 2} done={!!quote.sections.length && step > 2} onClick={() => setStep(2)} />
        <Step n={3} label="Sections" active={step === 3} done={step > 3} onClick={() => setStep(3)} />
        <Step n={4} label="Materials & Labour" active={step === 4} done={step > 4} onClick={() => setStep(4)} />
        <Step n={5} label="Review" active={step === 5} done={false} onClick={() => setStep(5)} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {step === 1 && (
            <div className="card-elev p-5" data-testid="step-customer">
              <div className="overline">Step 1</div>
              <h3 className="font-display text-2xl font-bold mt-1">Pick a customer</h3>
              <div className="grid sm:grid-cols-2 gap-3 mt-5">
                <label className="block sm:col-span-2">
                  <span className="overline">Customer</span>
                  <select className="rqp mt-1.5" value={quote.customer_id} onChange={(e) => setField("customer_id", e.target.value)} data-testid="quote-customer-select">
                    <option value="">— Select —</option>
                    {customers.map((c) => <option key={c.id} value={c.id}>{c.name} {c.postcode ? `(${c.postcode})` : ""}</option>)}
                  </select>
                </label>
                <label className="block sm:col-span-2">
                  <span className="overline">Quote title</span>
                  <input className="rqp mt-1.5" value={quote.title} onChange={(e) => setField("title", e.target.value)} data-testid="quote-title-input" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="overline">Site address</span>
                  <textarea rows={2} className="rqp mt-1.5" value={quote.site_address} onChange={(e) => setField("site_address", e.target.value)} data-testid="quote-site-input" />
                </label>
              </div>
              <div className="mt-5 flex justify-between">
                <Link to="/customers" className="text-xs text-[var(--rqp-orange)] font-semibold hover:underline">+ Add new customer</Link>
                <button className="btn-primary" onClick={() => (quote.customer_id ? setStep(2) : toast.error("Pick a customer"))} data-testid="step-1-next">Continue →</button>
              </div>
            </div>
          )}

          {step === 2 && (
            <PresetPicker
              value={quote.roof_type}
              category={quote.roof_category}
              onPick={quote.sections.length === 0 ? onPickPreset : onPickPresetReplace}
              onCategoryChange={(c) => setField("roof_category", c)}
              onSkip={onStartBlank}
            />
          )}

          {step === 3 && (
            <SectionsManager
              sections={quote.sections}
              perSection={perSection}
              warnings={audit.warnings || []}
              onChange={onSectionsChange}
              onBack={() => setStep(2)}
              onNext={() => setStep(4)}
            />
          )}

          {step === 4 && (
            <MaterialsTable
              quote={quote}
              materials={materials}
              preview={preview}
              dragIdx={dragIdx}
              setDragIdx={setDragIdx}
              reorderMaterial={reorderMaterial}
              removeMaterial={removeMaterial}
              updateMaterialRow={updateMaterialRow}
              addMaterialFromCatalog={addMaterialFromCatalog}
              setField={setField}
              onBack={() => setStep(3)}
              onNext={() => setStep(5)}
            />
          )}

          {step === 5 && (
            <div className="card-elev p-5" data-testid="step-review">
              <div className="overline">Step 5</div>
              <h3 className="font-display text-2xl font-bold mt-1">Review &amp; Export</h3>
              <p className="text-sm text-slate-500 mt-1">Final check, then save or download PDF.</p>

              <div className="grid sm:grid-cols-2 gap-4 mt-5">
                <div>
                  <div className="overline">Customer</div>
                  <div className="font-display text-lg font-semibold">{customers.find((c) => c.id === quote.customer_id)?.name || "—"}</div>
                  <div className="text-xs text-slate-500 mt-0.5 whitespace-pre-line">{quote.site_address || ""}</div>
                </div>
                <div>
                  <div className="overline">Roof</div>
                  <div className="font-display text-lg font-semibold">{selectedRoof?.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{quote.roof_category} · {quote.sections.length} section{quote.sections.length === 1 ? "" : "s"}</div>
                </div>
              </div>

              {/* Customer share link + deposit status (when saved) */}
              {existingId && quote.acceptance_token && (
                <div className="mt-5 border border-slate-200 rounded-sm p-4 bg-slate-50" data-testid="share-link-block">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="overline">Customer link</div>
                      <div className="text-xs text-slate-500 mt-0.5">Share this with your customer — they can view, sign and pay the deposit online.</div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-sm border ${quote.deposit_status === "paid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : quote.deposit_status === "pending" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-100 text-slate-700 border-slate-200"}`} data-testid="deposit-status-badge">
                        Deposit: {quote.deposit_status || "none"}
                      </span>
                      {quote.acceptance?.accepted_at && (
                        <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-sm border bg-blue-50 text-blue-700 border-blue-200">
                          Accepted by {quote.acceptance.accepted_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex items-stretch gap-2">
                    <input
                      readOnly
                      value={`${window.location.origin}/q/${quote.acceptance_token}`}
                      className="rqp font-mono-data text-xs"
                      data-testid="share-link-input"
                      onFocus={(e) => e.target.select()}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const url = `${window.location.origin}/q/${quote.acceptance_token}`;
                        const fallback = () => {
                          try {
                            const ta = document.createElement("textarea");
                            ta.value = url;
                            ta.style.position = "fixed";
                            ta.style.opacity = "0";
                            document.body.appendChild(ta);
                            ta.focus();
                            ta.select();
                            document.execCommand("copy");
                            document.body.removeChild(ta);
                            toast.success("Link copied to clipboard");
                          } catch {
                            toast.info("Copy not permitted — select and copy manually");
                          }
                        };
                        if (navigator.clipboard?.writeText) {
                          navigator.clipboard.writeText(url).then(
                            () => toast.success("Link copied to clipboard"),
                            fallback,
                          );
                        } else {
                          fallback();
                        }
                      }}
                      className="btn-secondary"
                      data-testid="share-link-copy"
                    >
                      Copy
                    </button>
                    <a
                      href={`/q/${quote.acceptance_token}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-primary"
                      data-testid="share-link-open"
                    >
                      Open
                    </a>
                  </div>
                </div>
              )}

              <div className="mt-5">
                <RoofPlanSVG sections={quote.sections} perSection={perSection} />
              </div>

              {/* BS 5534 / BS 5250 Specification Panel */}
              <div className="mt-5">
                <SpecificationsPanel specs={specs} sections={quote.sections} />
              </div>

              {/* Audit summary with per-warning Override button */}
              {(audit.warnings || []).length > 0 && (
                <div className="mt-5 border border-amber-200 bg-amber-50 rounded-sm p-3" data-testid="audit-summary">
                  <div className="overline text-amber-800">Compliance audit — {audit.summary?.warning || 0} warning, {audit.summary?.critical || 0} critical, {audit.summary?.info || 0} info</div>
                  <ul className="mt-2 space-y-1.5 text-xs text-amber-900">
                    {(audit.warnings || []).map((w, i) => {
                      const overridden = (quote.compliance_overrides || []).some((o) => o.rule === w.rule);
                      const wKey = `${w.section_name || "section"}-${w.rule || "rule"}-${i}`;
                      return (
                        <li key={wKey} className="flex items-start gap-2">
                          <span className="flex-1">
                            • <strong>[{w.section_name}]</strong> {w.message}{" "}
                            <span className="text-amber-700 uppercase tracking-wider text-[10px]">({w.standard}{w.clause ? ` · ${w.clause}` : ""})</span>
                          </span>
                          {overridden ? (
                            <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm" data-testid={`override-recorded-${i}`}>Override recorded</span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setOverrideModal({ open: true, warning: w })}
                              className="text-[10px] uppercase tracking-wider font-bold text-amber-900 bg-amber-100 hover:bg-amber-200 border border-amber-300 px-1.5 py-0.5 rounded-sm"
                              data-testid={`override-button-${i}`}
                              disabled={!existingId}
                              title={existingId ? "Record an override with reason" : "Save the quote first"}
                            >
                              Override
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  <div className="text-[10px] text-amber-800 mt-2 uppercase tracking-wider">Warnings do not block — you can save anyway. Each override is recorded in the audit trail and printed on the PDF.</div>
                </div>
              )}

              {/* Existing overrides list (audit trail surfaced) */}
              {(quote.compliance_overrides || []).length > 0 && (
                <div className="mt-4 border border-emerald-200 bg-emerald-50 rounded-sm p-3" data-testid="overrides-list">
                  <div className="overline text-emerald-800">Recorded overrides (audit trail)</div>
                  <ul className="mt-2 space-y-1.5 text-xs text-emerald-900">
                    {(quote.compliance_overrides || []).map((o, i) => (
                      <li key={`${o.rule || "rule"}-${o.accepted_at || i}`}>
                        <strong>{o.rule}</strong> — {o.reason}
                        <div className="text-[10px] uppercase tracking-wider text-emerald-700 mt-0.5">
                          {o.standard}{o.clause ? ` · ${o.clause}` : ""} · {o.accepted_by} · {o.accepted_at?.slice(0, 10)}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <label className="block mt-5">
                <span className="overline">Notes / Terms (override defaults)</span>
                <textarea rows={3} className="rqp mt-1.5" value={quote.terms || ""} onChange={(e) => setField("terms", e.target.value)} placeholder="Defaults applied if blank" />
              </label>

              <div className="mt-5 flex flex-wrap justify-between gap-2">
                <button className="btn-secondary" onClick={() => setStep(4)}>← Back</button>
                <div className="flex gap-2">
                  <button className="btn-secondary" onClick={save} disabled={saving}>{saving ? "Saving…" : (existingId ? "Save changes" : "Save quote")}</button>
                  <button className="btn-primary flex items-center gap-2" onClick={downloadPdf}><FilePdf size={14} weight="bold" /> Export PDF</button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar: live totals */}
        <div className="space-y-4">
          <TotalsSidebar preview={preview} warningsSummary={audit.summary} vatEnabled={quote.vat_enabled !== false} />
        </div>
      </div>

      <EmailQuoteDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        quote={emailDialogQuote}
        customer={selectedCustomer}
        onSent={() => {
          if (quote.status === "Draft") setField("status", "Sent");
        }}
      />

      <ComplianceOverrideModal
        open={overrideModal.open}
        onClose={() => setOverrideModal({ open: false, warning: null })}
        warning={overrideModal.warning}
        quoteId={existingId}
        onSaved={(updatedQuote) => {
          setQuote((q) => ({ ...q, compliance_overrides: updatedQuote.compliance_overrides || [] }));
        }}
      />
    </div>
  );
}
